import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, JSX, Ref, SubmitEvent } from "react";
import { useI18n } from "../i18n/I18nContext";
import { cx } from "./classNames";
import { FormErrorsContext } from "./formErrors";
import { formProblems } from "./formProblems";
import type { FormProblem } from "./formProblems";
import styles from "./ui.module.css";

/** Properties of {@link ValidatedForm}: those of a form, with a submit handler for valid input. */
export interface ValidatedFormProps extends Omit<
  ComponentPropsWithoutRef<"form">,
  "noValidate" | "onSubmit"
> {
  /** Reference to the form element, for example to move the focus after an own check. */
  readonly ref?: Ref<HTMLFormElement> | undefined;
  /**
   * Called when every control meets its constraints; the default action of the event is already
   * prevented. Further checks of the form (between fields, against loaded data) run here.
   *
   * @param event - The submit event.
   */
  readonly onSubmit: (event: SubmitEvent<HTMLFormElement>) => void;
}

/**
 * Passes a form element to a reference given by the caller.
 *
 * @param ref - The reference, a callback or an object, or nothing.
 * @param element - The form element, or `null` when it is removed.
 */
function assignRef(ref: Ref<HTMLFormElement> | undefined, element: HTMLFormElement | null): void {
  if (typeof ref === "function") {
    ref(element);
  } else if (ref !== undefined && ref !== null) {
    ref.current = element;
  }
}

/**
 * Form with one validation for every screen (F-60, UX-008, UX-111, WCAG 3.3.1, 3.3.3). The
 * browser's own bubbles are off (`noValidate`): they vanish after a moment, show one problem at a
 * time and speak the browser's language. Instead, the constraints of the controls (`required`,
 * `min`, `max`, `step`, `pattern`, `minLength`, e-mail fields) are checked here on submit. Each
 * problem appears at its field (`Field`, `ControlField` and `Checkbox` read it, tie it with
 * `aria-describedby` and set `aria-invalid`) and in an error summary at the end of the form, next
 * to the buttons just used: it counts the problems and lists each as a link to its field. The
 * focus moves to the first invalid control, and the number of problems is announced politely
 * afterwards. After a failed submit the messages follow the input. Only valid input reaches
 * `onSubmit` (ADR-0188).
 *
 * @param props - Properties of the form element and the submit handler.
 * @returns The form.
 */
export function ValidatedForm(props: ValidatedFormProps): JSX.Element {
  const { ref, onSubmit, onChange, children, ...formProps } = props;
  const { t, languageTag } = useI18n();
  const formRef = useRef<HTMLFormElement | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const focusTarget = useRef<HTMLElement | null>(null);
  const [problems, setProblems] = useState<readonly FormProblem[]>([]);
  const [checking, setChecking] = useState(false);
  const [failedSubmits, setFailedSubmits] = useState(0);
  const [reportedCount, setReportedCount] = useState(0);
  const summaryTitleId = useId();
  const errors = useMemo(
    () =>
      new Map(
        problems
          .filter((problem) => problem.controlId !== "")
          .map((problem) => [problem.controlId, problem.message] as const),
      ),
    [problems],
  );
  const attach = useCallback(
    (element: HTMLFormElement | null): void => {
      formRef.current = element;
      assignRef(ref, element);
    },
    [ref],
  );

  // The focus moves once the errors are rendered, so the control is announced with its error.
  useEffect(() => {
    if (failedSubmits > 0) {
      const target = focusTarget.current;

      if (target?.isConnected === true) {
        target.focus();
      } else {
        summaryRef.current?.focus();
      }
    }
  }, [failedSubmits]);

  const submit = (event: SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const found = formProblems(event.currentTarget, t, languageTag);

    setProblems(found);
    setChecking(found.length > 0);

    if (found.length > 0) {
      focusTarget.current = found[0]?.control ?? null;
      setReportedCount(found.length);
      setFailedSubmits((count) => count + 1);
    } else {
      onSubmit(event);
    }
  };

  // After a failed submit, the problems follow the input. They are read once the change is
  // rendered, so fields that appear or disappear with it are counted correctly.
  const change: NonNullable<ValidatedFormProps["onChange"]> = (event) => {
    onChange?.(event);

    if (checking) {
      queueMicrotask(() => {
        const form = formRef.current;

        if (form !== null) {
          setProblems(formProblems(form, t, languageTag));
        }
      });
    }
  };

  const form = (
    <form {...formProps} ref={attach} noValidate onSubmit={submit} onChange={change}>
      <FormErrorsContext value={errors}>
        {children}
        {problems.length === 0 ? null : (
          <div
            ref={summaryRef}
            role="group"
            aria-labelledby={summaryTitleId}
            tabIndex={-1}
            data-error-summary
            className={cx(styles["alert"], styles["alertDanger"], styles["errorSummary"])}
          >
            <p id={summaryTitleId}>
              <strong>{t("form.errorSummary", { count: problems.length })}</strong>
            </p>
            <ul>
              {problems.map((problem, index) => {
                const text =
                  problem.label === ""
                    ? problem.message
                    : t("form.problem", { label: problem.label, message: problem.message });
                const entry = (
                  <li key={`${String(index)}-${problem.controlId}`}>
                    {problem.controlId === "" ? (
                      text
                    ) : (
                      <a
                        href={`#${problem.controlId}`}
                        className={styles["linkButton"]}
                        onClick={(event) => {
                          event.preventDefault();
                          problem.control.focus();
                        }}
                      >
                        {text}
                      </a>
                    )}
                  </li>
                );

                return entry;
              })}
            </ul>
          </div>
        )}
        {/* The number of problems is announced after the first invalid field took the focus. */}
        <p role="status" className="visually-hidden">
          {problems.length === 0 ? null : (
            <span key={failedSubmits}>{t("form.errorSummary", { count: reportedCount })}</span>
          )}
        </p>
      </FormErrorsContext>
    </form>
  );

  return form;
}
